import { useFormikContext } from "formik";
import React from "react";
import _, { uniqueId } from "lodash";

import Child from "./Child";
import { FlatApplciationType } from "../../../types";

const Children: React.FC<{ id?: string }> = ({ id = "child" }) => {
  const { values, setFieldValue } = useFormikContext<FlatApplciationType>();
  const initialRenderRef = React.useRef(true);

  // Listen to changes to the `child_action` field
  React.useEffect(() => {
    if (Array.isArray(values.child_action) && !Array(...values.child_action).some((action) => id.includes(action as string))) {
      // Remove the children if the action is not selected
      setFieldValue("children", values.children?.filter((child) => !String(child.prp_id).startsWith(id)));
    }
  }, [values.child_action, setFieldValue, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get the children for this specific group based on ID prefix
  const childrenGroup = React.useMemo(
    () =>
      Array.isArray(values.children)
        ? values.children.filter(({ prp_id }) => String(prp_id).startsWith(id))
        : [],
    [values.children, id]
  );

  // Add a new child to the form
  const addChild = React.useCallback(() => {
    const newChild = {
      prp_id: uniqueId(id),
    };
    
    // Update the full children array correctly
    if (!Array.isArray(values.children) || values.children.length === 0) {
      setFieldValue("children", [newChild]);
    } else {
      // Preserve other children groups that don't match our ID prefix
      const otherChildren = values.children.filter(
        ({ prp_id }) => !String(prp_id).startsWith(id)
      );
      setFieldValue("children", [...otherChildren, ...childrenGroup, newChild]);
    }
  }, [values.children, childrenGroup, setFieldValue, id]);

  // Remove a child by ID
  const removeChild = React.useCallback(
    (childId: string) => {
      if (Array.isArray(values.children)) {
        setFieldValue(
          "children",
          values.children.filter(({ prp_id }) => prp_id !== childId)
        );
      }
    },
    [values.children, setFieldValue]
  );

  // Only add a child on initial mount if none exist for this group
  React.useEffect(() => {
    // Only execute on the first render
    if (initialRenderRef.current && _.isEmpty(childrenGroup)) {
      initialRenderRef.current = false;
      addChild();
    }
  }, [childrenGroup, addChild]);

  return (
    <>
      {childrenGroup.map(({ prp_id }, index) => {
        return (
          <Child
            key={String(prp_id)}
            childIndex={index}
            childId={String(prp_id)}
            addChild={addChild}
            removeChild={removeChild}
            isLastChild={index === childrenGroup.length - 1}
          />
        );
      })}
    </>
  );
};

export default Children;
